import { UIKit } from "@/shared/ui";
import {
	getCatalogItem,
	clampItemSize,
	sectionOfItem,
	SOCKET_BLOCK_IDS,
	socketBlockCount,
	switchKeyCount,
	type CatalogItem,
} from "@/shared/catalog";
import { addLightingLink, cableEndCatalogItem, lightsOfSwitch } from "@/widgets/electrical";
import { isPanel, isSwitch } from "@/shared/catalog";
import { useFixturesStore, useWiringStore, wallFixtureOwner } from "@/shared/store/layers";
import { SelectionKind, useEditorStore } from "@/widgets/editor";
import { useProjectStore } from "@/shared/store";
import { remove } from "@/shared/store/actions";
import { CatalogSectionKind, itemDimensions, type Fixture } from "@/shared/plan";
import { findFixture } from "@/shared/plan";
import { ItemSizeFields } from "./ItemSizeFields";
import { ObjectComments } from "./ObjectComments";

export type PropertiesPanelCatalogItem = CatalogItem;

export const FixtureProperties = () => {
	const layers = useProjectStore((s) => s.project.layers);
	const graph = layers.walls;
	const selection = useEditorStore((s) => s.selection);
	const setSelection = useEditorStore((s) => s.setSelection);
	const pushHistory = useProjectStore((s) => s.pushHistory);

	const electricalFixtures = useFixturesStore((s) => s.items);
	const updateFixtureElectrical = useFixturesStore((s) => s.updateFixture);

	// Хелпер для updateFixture в зависимости от каталога
	const updateFixture = (id: string, patch: Partial<Fixture>) => {
		if (electricalFixtures[id]) {
			updateFixtureElectrical(id, patch);
		} else {
			wallFixtureOwner(id)?.getState().updateFixture(id, patch);
		}
	};

	const onDelete = () => {
		// Удаление кнопкой отменяется по Ctrl+Z, как и клавишей Delete
		pushHistory();
		remove(selection);
		setSelection(null);
	};

	if (!selection) return null;
	if (selection.kind === SelectionKind.Fixture) {
		const found = findFixture(layers, selection.id);
		if (!found) return null;
		const { fixture } = found;
		const wall = graph.walls[fixture.wallId];
		const info = getCatalogItem(fixture.ref);
		const dimensions = info ? itemDimensions(fixture, info) : null;
		const resizable = sectionOfItem(fixture.ref)?.canChangeDimensions ?? false;
		const socketCount =
			fixture.ref.catalog === CatalogSectionKind.Electrical
				? socketBlockCount(fixture.ref.item)
				: null;
		// Выключатель: какие светильники он зажигает — по кабелям, сквозь светильники
		const controlled = isSwitch(info)
			? lightsOfSwitch(
					layers.electrical,
					layers.wiring.items,
					fixture.id,
					layers.wiring.connections,
				)
			: null;

		return (
			<>
				<UIKit.Text className="text-sm font-semibold tracking-label text-ink uppercase">
					{info?.name ?? "Объект удалён из каталога"}
				</UIKit.Text>
				{info && dimensions && (
					<ItemSizeFields
						info={info}
						dimensions={dimensions}
						disabled={!resizable}
						onResize={(axis, value) => {
							// Объект на стене прижат к ней задом сам, ширина растёт в обе стороны
							pushHistory();
							const size = clampItemSize(info, axis, value);
							// Вернули каталожный размер — личный стираем: слой хранит только правки пользователя
							updateFixture(fixture.id, { [axis]: size === info[axis] ? undefined : size });
						}}
					/>
				)}
				{socketCount !== null && (
					<UIKit.Select
						label="Розеток в блоке"
						value={socketCount}
						options={SOCKET_BLOCK_IDS.map((_, index) => ({
							value: index + 1,
							label: String(index + 1),
						}))}
						onChange={(count) => {
							const item = SOCKET_BLOCK_IDS[count - 1];
							if (item)
								useProjectStore.getState().recordEdit(() =>
									updateFixture(fixture.id, {
										ref: { ...fixture.ref, item },
										width: undefined,
										height: undefined,
										depth: undefined,
									}),
								);
						}}
					/>
				)}
				<UIKit.NumberField
					label="Высота от пола"
					value={fixture.elevation}
					min={0}
					max={wall ? wall.height : 3000}
					step={50}
					onChange={(elevation) => updateFixture(fixture.id, { elevation })}
				/>
				{isPanel(info) && (
					<UIKit.Select
						label="Питание щитка"
						value={fixture.supplyPhases ?? 0}
						options={[
							{ value: 0, label: "Неизвестно" },
							{ value: 1, label: "Однофазное" },
							{ value: 3, label: "Трёхфазное" },
						]}
						onChange={(phases) =>
							useProjectStore.getState().recordEdit(() =>
								updateFixture(fixture.id, {
									supplyPhases: phases === 1 || phases === 3 ? phases : undefined,
								}),
							)
						}
					/>
				)}
				<div className="flex gap-1.5">
					<UIKit.Button
						type="button"
						className="flex-1 rounded-lg border border-border bg-surface px-2 py-1.75 text-xs text-ink hover:border-accent/50 hover:text-ink"
						title="Перенести на противоположную грань стены"
						onClick={() =>
							useProjectStore
								.getState()
								.recordEdit(() => updateFixture(fixture.id, { side: fixture.side === 1 ? -1 : 1 }))
						}
					>
						Другая сторона стены
					</UIKit.Button>
				</div>

				{controlled && (
					<div className="flex flex-col gap-1 rounded-lg bg-bg px-2.5 py-2 text-xs text-ink">
						<UIKit.Select
							label="Управление светом"
							value={fixture.lightingControl ?? "cables"}
							options={[
								{ value: "cables", label: "По кабелям (старый способ)" },
								{ value: "connections", label: "По связям выключателя" },
							]}
							onChange={(mode) =>
								useProjectStore.getState().recordEdit(() => {
									if (mode === "connections" && fixture.lightingControl !== "connections") {
										for (const lightId of controlled)
											addLightingLink(
												layers.electrical,
												layers.wiring.items,
												fixture.id,
												lightId,
												"arc",
											);
									}
									updateFixture(fixture.id, {
										lightingControl: mode === "connections" ? "connections" : undefined,
									});
								})
							}
						/>
						<UIKit.Text as="span">
							Группа освещения:{" "}
							<b className={controlled.length > 0 ? "text-ink" : "text-amber-700"}>
								{controlled.length > 0 ? `${controlled.length} свет.` : "пусто"}
							</b>
						</UIKit.Text>
						{fixture.lightingControl === "connections" && switchKeyCount(fixture.ref.item) > 1 ? (
							Object.values(layers.wiring.connections)
								.filter(
									(connection) => connection.from === fixture.id || connection.to === fixture.id,
								)
								.map((connection) => {
									const lightId = connection.from === fixture.id ? connection.to : connection.from;
									return (
										<div
											key={connection.id}
											className="flex flex-col gap-1 border-t border-border pt-1"
										>
											<UIKit.Button
												type="button"
												className="text-left text-xs"
												onClick={() =>
													setSelection({
														kind: layers.electrical.fixtures[lightId]
															? SelectionKind.Fixture
															: SelectionKind.Light,
														id: lightId,
													})
												}
											>
												{cableEndCatalogItem(layers.electrical, lightId)?.name ?? "Светильник"} ·{" "}
												{lightId}
											</UIKit.Button>
											<UIKit.Select
												label="Клавиша"
												value={connection.key ?? 1}
												options={([1, 2, 3] as const)
													.filter((key) => key <= switchKeyCount(fixture.ref.item))
													.map((key) => ({ value: key, label: `Клавиша ${key}` }))}
												onChange={(key) =>
													useProjectStore
														.getState()
														.recordEdit(() =>
															useWiringStore
																.getState()
																.updateLightingConnection(connection.id, { key }),
														)
												}
											/>
										</div>
									);
								})
						) : controlled.length > 0 ? (
							<ul className="m-0 flex list-none flex-col gap-0.5 p-0">
								{controlled.map((id) => (
									<li key={id} className="truncate">
										• {cableEndCatalogItem(layers.electrical, id)?.name ?? "Светильник"}
									</li>
								))}
							</ul>
						) : (
							<UIKit.Text as="span">
								Добавьте связь выключателя со светильником в разделе освещения.
							</UIKit.Text>
						)}
					</div>
				)}

				<ObjectComments kind="fixture" id={fixture.id} />
				<UIKit.Button
					type="button"
					className="mt-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-sm text-danger hover:bg-red-100 dark:border-red-500/50 dark:bg-red-950/30 dark:hover:bg-red-950/45"
					onClick={onDelete}
				>
					Удалить объект
				</UIKit.Button>
			</>
		);
	}

	return null;
};
